import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { UserProfile } from '@/types/models';
import { useAuth } from './AuthContext';
import { db } from '@/lib/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

interface UserProfileContextType {
  userProfile: UserProfile | null;
  updateUserProfile: (profile: Partial<UserProfile>) => void;
  loading: boolean;
}

const UserProfileContext = createContext<UserProfileContextType | undefined>(undefined);

export const useUserProfile = (): UserProfileContextType => {
  const context = useContext(UserProfileContext);
  if (!context) {
    throw new Error('useUserProfile debe ser utilizado dentro de un UserProfileProvider');
  }
  return context;
};

interface UserProfileProviderProps {
  children: ReactNode;
}

export const UserProfileProvider: React.FC<UserProfileProviderProps> = ({ children }) => {
  const { currentUser, userRole } = useAuth();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const setDefaultProfile = useCallback(async () => {
    if (!currentUser) return;
    
    const profileKey = `userProfile_${currentUser.uid}`;
    
    // First try to fetch from Firestore
    try {
      const docRef = doc(db, 'user_profiles', currentUser.uid);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const firestoreProfile = docSnap.data() as UserProfile;
        // Make sure date objects are converted back to Dates if stored as Timestamps
        const updatedAtVal = firestoreProfile.updatedAt as unknown as { toDate?: () => Date };
        if (updatedAtVal && typeof updatedAtVal.toDate === 'function') {
          firestoreProfile.updatedAt = updatedAtVal.toDate();
        }
        
        // --- OVERRIDE FOR ADMINS ---
        if (userRole === 'admin' || userRole === 'superadmin') {
          if (firestoreProfile.subscriptionPlan !== 'pro' && firestoreProfile.subscriptionPlan !== 'enterprise') {
            firestoreProfile.subscriptionPlan = 'pro';
            await setDoc(docRef, { subscriptionPlan: 'pro' }, { merge: true }).catch(console.error);
          }
        }
        
        setUserProfile(firestoreProfile);
        localStorage.setItem(profileKey, JSON.stringify(firestoreProfile));
        return;
      }
    } catch (error) {
      console.error('Error fetching profile from Firestore:', error);
    }
    
    // If not in Firestore or error, try local storage
    const savedProfile = localStorage.getItem(profileKey);
    if (savedProfile) {
      try {
        const parsed = JSON.parse(savedProfile);
        parsed.updatedAt = new Date(parsed.updatedAt);
        
        if (userRole === 'admin' || userRole === 'superadmin') {
          parsed.subscriptionPlan = 'pro';
        }
        
        setUserProfile(parsed);
        // Upload the cached profile to Firestore so it exists there
        await setDoc(doc(db, 'user_profiles', currentUser.uid), parsed, { merge: true }).catch(console.error);
        return;
      } catch (error) {
        console.error('Error parsing local profile:', error);
      }
    }

    // Finally fallback to default
    const defaultProfile: UserProfile = {
      id: currentUser.uid,
      name: currentUser.displayName || 'Usuario',
      artistName: currentUser.displayName || '',
      email: currentUser.email || 'usuario@example.com',
      phone: '+506 0000-0000',
      logoUrl: '',
      subscriptionPlan: (userRole === 'admin' || userRole === 'superadmin') ? 'pro' : 'free',
      updatedAt: new Date(),
    };
    
    setUserProfile(defaultProfile);
    localStorage.setItem(profileKey, JSON.stringify(defaultProfile));
    await setDoc(doc(db, 'user_profiles', currentUser.uid), defaultProfile, { merge: true }).catch(console.error);
  }, [currentUser, userRole]);

  useEffect(() => {
    const initProfile = async () => {
      if (currentUser) {
        await setDefaultProfile();
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    };
    initProfile();
  }, [currentUser, setDefaultProfile]);

  const updateUserProfile = async (profileData: Partial<UserProfile>) => {
    if (!userProfile || !currentUser) return;

    const updatedProfile = {
      ...userProfile,
      ...profileData,
      updatedAt: new Date(),
    };

    setUserProfile(updatedProfile);
    
    const profileKey = `userProfile_${currentUser.uid}`;
    localStorage.setItem(profileKey, JSON.stringify(updatedProfile));
    
    // Upload changes to Firestore
    try {
      await setDoc(doc(db, 'user_profiles', currentUser.uid), updatedProfile, { merge: true });
    } catch (error) {
      console.error('Error updating profile in Firestore:', error);
    }
  };

  const value = {
    userProfile,
    updateUserProfile,
    loading,
  };

  return (
    <UserProfileContext.Provider value={value}>
      {children}
    </UserProfileContext.Provider>
  );
};